import React, { useState, useEffect } from 'react';
import API from '../api';
import SEO from "../components/SEO";

const PostJobPage = () => {
    const [form, setForm] = useState({
        company: '',
        position: '',
        location: '',
        jobType: '',
        description: '',
        applicationLink: '',
        contactEmail: '',
        deadline: '',
    });
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        setError(null);

        // Validate required fields
        const requiredFields = ['company', 'position', 'contactEmail', 'description'];
        const missingFields = requiredFields.filter(field => !form[field]);
        
        if (missingFields.length > 0) {
            setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
            setStatus(null);
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.contactEmail)) {
            setError('Please provide a valid email address');
            setStatus(null);
            return;
        }

        try {
            if (API && API.post) {
                const response = await API.post('/jobs', form);
                if (response.data.success) {
                    setStatus('success');
                    setForm({
                        company: '',
                        position: '',
                        location: '',
                        jobType: '',
                        description: '',
                        applicationLink: '',
                        contactEmail: '',
                        deadline: '',
                    });
                }
            } else {
                console.log('Job post payload', form);
                setStatus('success');
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Failed to post job. Please try again.');
            setStatus('error');
        }
    };

    const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://africaresearchuniversity.com/alumni/jobs/post';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': 'Post a Job - Africa Research University',
        'description': 'Post job opportunities to recruit ARU graduates.',
        'url': pageUrl
    };

    return (
        <section className="min-h-screen py-16 bg-gray-50">
            <SEO title="Post a Job — Africa Research University" description="Post job opportunities to recruit ARU graduates." image="/assets/img/arulogo.png" url={pageUrl} jsonLd={jsonLd} />
            <div className="max-w-3xl my-16 mx-auto px-6">
                <div className="flex items-center mb-6">
                    <a href="/alumni?tab=get-involved" className="flex items-center text-blue-600 hover:text-blue-800">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Alumni
                    </a>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mb-4">Post a Job</h1>
                <p className="text-gray-600 mb-6">Employ our graduates by posting job opportunities here.</p>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-600">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <input 
                                name="company" 
                                value={form.company} 
                                onChange={handleChange} 
                                placeholder="Company *" 
                                className="p-3 border rounded w-full" 
                                required 
                            />
                        </div>
                        <div>
                            <input 
                                name="position" 
                                value={form.position} 
                                onChange={handleChange} 
                                placeholder="Position *" 
                                className="p-3 border rounded w-full" 
                                required 
                            />
                        </div>
                        <div>
                            <input 
                                name="location" 
                                value={form.location} 
                                onChange={handleChange} 
                                placeholder="Location" 
                                className="p-3 border rounded w-full" 
                            />
                        </div>
                        <div>
                            <input 
                                name="jobType" 
                                value={form.jobType} 
                                onChange={handleChange} 
                                placeholder="Job type (full-time/part-time)" 
                                className="p-3 border rounded w-full" 
                            />
                        </div>
                        <div className="md:col-span-2">
                            <textarea 
                                name="description" 
                                value={form.description} 
                                onChange={handleChange} 
                                placeholder="Job description *" 
                                className="p-3 border rounded w-full min-h-[100px]" 
                                required 
                            />
                        </div>
                        <div>
                            <input 
                                name="applicationLink" 
                                value={form.applicationLink} 
                                onChange={handleChange} 
                                placeholder="Application link" 
                                className="p-3 border rounded w-full" 
                            />
                        </div>
                        <div>
                            <input 
                                name="contactEmail" 
                                type="email" 
                                value={form.contactEmail} 
                                onChange={handleChange} 
                                placeholder="Your alumni email address *" 
                                className="p-3 border rounded w-full" 
                                required 
                            />
                            <p className="text-sm text-gray-500 mt-1">Must be your registered alumni email</p>
                        </div>
                        <div>
                            <input 
                                name="deadline" 
                                type="date" 
                                value={form.deadline} 
                                onChange={handleChange} 
                                placeholder="Application deadline" 
                                className="p-3 border rounded w-full" 
                            />
                            <p className="text-sm text-gray-500 mt-1">Application deadline (optional)</p>
                        </div>
                    </div>

                    <p className="mt-4 text-sm text-gray-500">* Required fields</p>

                    <div className="mt-4 flex items-center gap-3">
                        <button 
                            type="submit" 
                            className="px-5 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                            disabled={status === 'submitting'}
                        >
                            {status === 'submitting' ? 'Posting...' : 'Post Job'}
                        </button>
                        {status === 'success' && (
                            <span className="text-sm text-green-600">
                                Job posted successfully! It will be reviewed before being published.
                            </span>
                        )}
                    </div>
                </form>
            </div>
        </section>
    );
};

export default PostJobPage;
